/*
 * Find An Expert (dealer locator) � client behavior only.
 *
 * The heavy lifting (calling the locator service, shaping the data, and rendering the
 * dealer cards) now happens server-side: the form POSTs to FindAnExpertPageController
 * .SearchDealers, which returns the _DealerResults partial. This module only wires up the
 * parts that must live in the browser:
 *   - the Google Map (markers + info windows) built from the returned markup's data-* attrs
 *   - "Use My Location" geolocation
 *   - the AJAX form submit + result injection
 *   - GTM analytics events
 *   - the "Make My Expert" auth gate: open the shared login modal when needed, then let
 *     the server do the add/remove work and repaint the button from its response
 *
 * All favorites business logic (single-favorite enforcement, which card is saved) lives
 * server-side in FindAnExpertPageController; this module only forwards the click and
 * reflects the returned state.
 */
import {
  dispatchSiteEvent,
  siteEventNames,
} from './events/site-events';
import { launchLoginModal } from './modules/consumer-account/login-modal';

const dealerGridDiv = document.getElementById('dealerGrid');
const dealerResultsHeading = document.getElementById('dealerResultsHeading');
const searchBtn = document.querySelector('.map-search-button');
const searchBtnDefaultVal = searchBtn ? searchBtn.value : '';
const mapDiv = document.getElementById('locatorMap');
const noContentDiv = document.getElementById('locatorMapNoContent');
const mapInit = window.locatorMap;
const requestUrl = window.searchDealersPath;
const { jsApiKey } = window;

let map = null;
let bounds = null;
let markers = [];
let activeMarker;
let userLocationData;
let submitErrorText = '';

const markerIcon = active => ({
  url: `${window.imagePath}icon-marker${active ? '-active' : ''}.svg`,
  scaledSize: new google.maps.Size(36, 36), /* eslint-disable-line no-undef */
});

const disableSearchButton = () => {
  searchBtn.setAttribute('disabled', 'disabled');
  searchBtn.value = 'Searching...';
};

const enableSearchButton = () => {
  searchBtn.removeAttribute('disabled');
  searchBtn.value = searchBtnDefaultVal;
};

const getValidAddress = str => str !== '';

const showMap = (bool) => {
  if (bool) {
    mapDiv.classList.add('locator-map-expanded');
    noContentDiv.classList.add('hidden');
  } else {
    mapDiv.classList.remove('locator-map-expanded');
    noContentDiv.classList.remove('hidden');
  }
};

const initMap = () => {
  /* eslint-disable no-undef */
  bounds = new google.maps.LatLngBounds();
  map = new google.maps.Map(mapDiv, {
    center: new google.maps.LatLng(43.1162759, -89.5101693),
  });
  /* eslint-enable no-undef */
};

const appendMapScript = () => {
  const script = document.createElement('script');
  // callback=initMap targets the global window.initMap defined in the page, which flips
  // window.locatorMap.init once the Maps API is ready.
  script.src = `https://maps.googleapis.com/maps/api/js?key=${jsApiKey}&callback=initMap`;
  document.body.appendChild(script);
};

const adjustZoomAfterBounds = () => {
  const listener = google.maps.event.addListener(map, 'idle', () => { /* eslint-disable-line no-undef */
    if (map.getZoom() > 13) {
      map.setZoom(13);
    }
    google.maps.event.removeListener(listener); /* eslint-disable-line no-undef */
  });
};

const buildInfoWindowHtml = (card) => {
  const d = card.dataset;
  const addr1 = d.addressLine1 ? `${d.addressLine1}<br />` : '';
  const addr2 = d.addressLine2 ? `${d.addressLine2}<br />` : '';
  const website = d.website
    ? `<a class="website-link" href="${d.website}" target="_blank" data-name="${d.businessName}">Website</a><br />`
    : '';
  const dealerType = d.shopAtHome === 'true' ? 'Design Consultant' : 'Showroom';
  return `<div class="dealer-card info-window-card">
    <div class="dealer-card--content">
      <div class="dealer-card--name heading--sm">${d.businessName}</div>
      <div class="nav--secondary m-b-xxs">${dealerType}</div>
      <div class="dealer-card--contact m-b-xs">
        <p class="body--small driving-distance">${d.drivingDistance} mi</p>
        ${addr1}${addr2}
        <span>${d.city}, ${d.state} ${d.postalCode}</span><br />
        <a class="phone-link" href="tel:${d.phoneNumber}" data-name="${d.businessName}">${d.phoneNumber}</a><br />
      </div>
      ${website}
    </div>
  </div>`;
};

const updateMarkers = () => {
  markers.forEach(item => item.setMap(null));
  markers = [];
  bounds = new google.maps.LatLngBounds(); /* eslint-disable-line no-undef */

  const cards = dealerGridDiv.querySelectorAll('.dealer-card');
  cards.forEach((card) => {
    const position = new google.maps.LatLng(/* eslint-disable-line no-undef */
      parseFloat(card.dataset.latitude),
      parseFloat(card.dataset.longitude),
    );

    const marker = new google.maps.Marker({ /* eslint-disable-line no-undef */
      position,
      animation: google.maps.Animation.DROP, /* eslint-disable-line no-undef */
      map,
      label: { text: card.dataset.label, color: 'white' },
      icon: markerIcon(false),
    });

    const infowindow = new google.maps.InfoWindow({ content: '', maxWidth: 240 }); /* eslint-disable-line no-undef */

    marker.addListener('click', () => {
      if (activeMarker !== undefined) {
        activeMarker.setIcon(markerIcon(false));
      }
      marker.setIcon(markerIcon(true));
      infowindow.setContent(buildInfoWindowHtml(card));
      infowindow.open(map, marker);
      activeMarker = marker;
    });

    markers.push(marker);
    map.fitBounds(bounds.extend(position));
    adjustZoomAfterBounds();
  });
};

const displayNoResultsMessage = () => {
  showMap(false);
  bounds = null;
  markers.forEach(item => item.setMap(null));
  markers = [];
  dealerGridDiv.innerHTML = '';
  dealerResultsHeading.classList.add('hidden');
  noContentDiv.innerHTML = window.noResultsMessage;
  noContentDiv.focus();
};

const renderResults = (html) => {
  dealerGridDiv.innerHTML = html;
  const count = dealerGridDiv.querySelectorAll('.dealer-card').length;

  if (count === 0) {
    displayNoResultsMessage();
    return;
  }

  if (mapInit.init === true && map === null) {
    initMap();
  }
  showMap(true);
  updateMarkers();

  dealerResultsHeading.classList.remove('hidden');
  dealerResultsHeading.innerHTML = `Results: ${count} Dealers`;
  dealerResultsHeading.focus();

  // - 90px to account for header
  window.scrollTo({ top: mapDiv.getBoundingClientRect().top + (window.scrollY - 90), behavior: 'smooth' });
};

const requestLocationData = (options) => {
  const {
    address = '',
    latitude = '',
    longitude = '',
    radius,
  } = options;
  const dl = (window.dataLayer && window.dataLayer[0]) || {};

  const params = new URLSearchParams({
    latitude,
    longitude,
    address,
    radius,
    utmcampaign: dl.utmCampaign || '',
    utmcontent: dl.utmContent || '',
    utmmedium: dl.utmMedium || '',
    utmsource: dl.utmSource || '',
    utmterm: dl.utmTerm || '',
  });

  fetch(requestUrl, {
    method: 'POST',
    headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
    body: params.toString(),
  })
    .then(response => response.text())
    .then((html) => {
      enableSearchButton();
      renderResults(html);
    })
    .catch(() => {
      enableSearchButton();
      displayNoResultsMessage();
    });

  dispatchSiteEvent(siteEventNames.ON_DEALER_SEARCH, {
    latitude,
    longitude,
    value: radius,
    zipcode: address,
  });
};

const onSetLocationSuccess = (data) => {
  const radius = document.querySelector('#Radius').value;
  document.querySelector('#AddressOrZipCode').value = '';
  userLocationData = data;
  requestLocationData({
    latitude: userLocationData.coords.lat,
    longitude: userLocationData.coords.lng,
    radius,
  });
};

const locateDealers = () => {
  disableSearchButton();
  const addrDiv = document.querySelector('[for="AddressOrZipCode"]');
  const address = document.querySelector('#AddressOrZipCode').value;
  const radius = document.querySelector('#Radius').value;
  const validOpt1 = getValidAddress(address) && radius !== '';
  const validOpt2 = userLocationData !== null && userLocationData !== undefined && radius !== '';
  const errorMessageDiv = document.querySelector('.dealer-locator .error-message');

  if (validOpt1 || validOpt2) {
    errorMessageDiv.classList.add('hidden');
    addrDiv.classList.remove('error');
    submitErrorText = '';
    if (validOpt1) {
      requestLocationData({ address, radius });
    } else {
      requestLocationData({
        latitude: userLocationData.coords.lat,
        longitude: userLocationData.coords.lng,
        radius,
      });
    }
    return;
  }

  if (!getValidAddress(address) && radius === '') {
    submitErrorText = 'Enter a valid address or zip code and select a radius';
  } else if (radius === '') {
    submitErrorText = 'Select a radius';
  } else if (!getValidAddress(address)) {
    submitErrorText = 'Enter a valid address or zip code';
    addrDiv.classList.add('error');
  }
  errorMessageDiv.innerHTML = submitErrorText;
  errorMessageDiv.classList.remove('hidden');
  enableSearchButton();
};

// Reflect the saved state on a single "Make My Expert" button. The two label strings are
// supplied by the server via data-label-* so no copy lives in JS.
const setExpertButtonState = (button, isSaved) => {
  const label = button.querySelector('.savebutton--label');
  button.classList.toggle('is-saved', isSaved);
  button.setAttribute('aria-pressed', isSaved ? 'true' : 'false');

  if (label) {
    const srOnly = label.querySelector('.sr-only');
    label.textContent = isSaved ? button.dataset.labelRemove : button.dataset.labelAdd;
    if (srOnly) {
      label.appendChild(srOnly);
    }
  }
};

// Only one dealer can be the favorite, so repaint every button from the single record id
// the server reports as saved (null when nothing is saved).
const repaintExpertButtons = (favoriteRecordId) => {
  document.querySelectorAll('.savebutton').forEach((button) => {
    const isSaved = favoriteRecordId !== null
      && favoriteRecordId !== undefined
      && button.dataset.recordId === favoriteRecordId;
    setExpertButtonState(button, isSaved);
  });
};

const postExpertToggle = (button) => {
  const params = new URLSearchParams({
    recordId: button.dataset.recordId || '',
    shipToAccount: button.dataset.shipToAccount || '',
  });

  button.setAttribute('disabled', 'disabled');

  fetch(window.toggleExpertPath, {
    method: 'POST',
    headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
    body: params.toString(),
  })
    .then(response => response.json())
    .then((data) => {
      button.removeAttribute('disabled');
      if (data.requiresAuth) {
        // Session was not established (or expired); prompt to log in, then retry.
        promptLoginThenToggle(button); // eslint-disable-line no-use-before-define
        return;
      }
      if (data.success) {
        repaintExpertButtons(data.favoriteRecordId || null);
      }
    })
    .catch(() => {
      button.removeAttribute('disabled');
    });
};

const promptLoginThenToggle = (button) => {
  launchLoginModal({
    type: 'location',
    callbacks: {
      onLoginSuccess: () => postExpertToggle(button),
      onLoginFail: () => { },
    },
  });
};

const onExpertButtonClick = (button) => {
  if (window.isAuthenticated) {
    postExpertToggle(button);
  } else {
    promptLoginThenToggle(button);
  }
};

const dealerPhoneClick = (dealer, phone) => {
  dispatchSiteEvent(siteEventNames.DEALER_PHONE_CLICK, { dealer, phone });
};

const dealerWebsiteClick = (dealer) => {
  dispatchSiteEvent(siteEventNames.DEALER_WEBSITE_CLICK, { dealer });
};

const init = () => {
  appendMapScript();

  // Single submit handler covers both the search button and the Enter key; the form
  // still posts to SearchDealers as a no-JS fallback, but here we take over with AJAX.
  document.querySelector('.locator-form').addEventListener('submit', (event) => {
    event.preventDefault();
    locateDealers();
  });

  document.querySelector('.use-my-location-button').addEventListener('click', () => {
    disableSearchButton();
    window.locationUtils.getDeviceLocation(
      jsApiKey,
      { success: onSetLocationSuccess },
    );
  });

  document.addEventListener('click', (event) => {
    const btn = event.target;

    // The save button wraps nested spans/divs, so resolve the button from the click target.
    const saveButton = btn.closest ? btn.closest('.savebutton') : null;
    if (saveButton) {
      onExpertButtonClick(saveButton);
      return;
    }

    if (btn.classList.contains('phone-link')) {
      dealerPhoneClick(btn.getAttribute('data-name'), btn.textContent);
    } else if (btn.classList.contains('website-link')) {
      dealerWebsiteClick(btn.getAttribute('data-name'));
    }
  });
};

export default {
  init,
};

if (document.querySelector('.find-an-expert-page')) {
  init();
}
